import React, { useState, useEffect } from 'react';
import { FaBars, FaCartPlus, FaCalendarAlt, FaGift, FaShoppingBasket, FaChartBar, FaUserFriends, FaCogs, FaTag, FaMoneyBillWave, FaSignOutAlt, FaListUl,FaListOl,FaStore } from 'react-icons/fa';
import { 
  Box, 
  CssBaseline, 
  AppBar, 
  Toolbar, 
  IconButton, 
  Typography, 
  Drawer, 
  Divider, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  SvgIcon, 
  Avatar, 
  Link, 
  Button,
  useMediaQuery,
  Container,
  Select,
  MenuItem
} from '@mui/material';
import { Link as DomLink, useLocation } from 'react-router-dom';
import { deepPurple } from '@mui/material/colors';
import { useHistory } from 'react-router-dom';
import { auth } from './firebase';
import { useAppContext } from './AppContext';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { useTheme } from '@mui/material/styles';
import packageJson from '../package.json';

const drawerWidth = 240;

const Layout = ({ children }) => {
  const location = useLocation();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const history = useHistory();
  const { userName, userRole, userId, isOnShift, credit, shopId, setCredit, useShiftSystem, setUserId, t, language, setLanguage } = useAppContext();
  
  const iconStyle = { color: 'white', fontSize: '4rem' };
  
  const buttons = [
    { href: '/newsale', icon: <FaStore style={iconStyle} />, label: t('New Sale'), roles: ['cashier', 'owner', 'manager'] },
    { href: '/viewsales', icon: <FaCartPlus style={iconStyle} />, label: t('View Sales'), roles: ['cashier', 'owner', 'manager'] },
    { href: '/neworder', icon: <FaCartPlus style={iconStyle} />, label: t('New Order'), roles: ['cashier', 'owner', 'manager'] },
    { href: '/vieworders', icon: <FaListUl style={iconStyle} />, label: t('View Orders'), roles: ['owner', 'manager', 'cashier'] },
    { href: '/manageorders', icon: <FaCalendarAlt style={iconStyle} />, label: t('Manage Orders'), roles: ['operator', 'owner', 'manager'] },
    { href: '/viewstock', icon: <FaListOl style={iconStyle} />, label: t('View Stock'), roles: ['cashier','operator', 'owner', 'manager'] },
    { href: '/managestock', icon: <FaListOl style={iconStyle} />, label: t('Manage Stock'), roles: ['operator', 'owner', 'manager'] },
    { href: '/releaseorders', icon: <FaGift style={iconStyle} />, label: t('Release Orders'), roles: ['cashier', 'owner', 'manager'] },
    { href: '/products', icon: <FaShoppingBasket style={iconStyle} />, label: t('Products'), roles: ['owner', 'manager'] },
    { href: '/promotion', icon: <FaTag style={iconStyle} />, label: t('Promotion Codes'), roles: ['owner', 'manager'] },
    { href: '/reports', icon: <FaChartBar style={iconStyle} />, label: t('Reports'), roles: ['owner', 'manager'] },
    { href: '/users', icon: <FaUserFriends style={iconStyle} />, label: t('Users'), roles: ['owner', 'manager'] },
    { href: '/settings', icon: <FaCogs style={iconStyle} />, label: t('Settings'), roles: ['owner'] },
  ];

  const filteredButtons = buttons.filter(button => button.roles.includes(userRole));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = () => {
    if (!isLargeScreen) {
      setMobileOpen(false);
    }
  };

  const handleSignOut = async () => {
    setUserId(null);
    localStorage.removeItem('authToken');
    await auth.signOut();
    history.push('/login');
  };

  useEffect(() => {
    const creditRef = doc(db, 'shops', shopId);
    const unsubscribe = onSnapshot(creditRef, (doc) => {
      if (doc.exists()) {
        setCredit(doc.data().credit);
      } else {
        console.log('No such document!');
      }
    });

    return () => unsubscribe();
  }, [shopId, setCredit]);

  const drawerContent = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <List sx={{ mt: 2 }}>
        {filteredButtons.map((button, index) => (
          <ListItem 
            button 
            component={DomLink} 
            to={button.href} 
            key={index}
            onClick={handleNavigation}
            selected={location.pathname === button.href}
          >
            <ListItemIcon>
              <SvgIcon component={button.icon.type} inheritViewBox />
            </ListItemIcon>
            <ListItemText primary={button.label} />
          </ListItem>
        ))}
      </List>

      <div style={{ marginTop: 'auto', padding: '16px' }}>
        <Divider />
        <Box display="flex" alignItems="center" padding="16px">
          <Avatar alt={userName} src="/path-to-profile-picture.jpg" sx={{ bgcolor: deepPurple[500] }} />
          <Typography variant="body1" sx={{ marginLeft: 2 }}>
            {userName}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" paddingLeft="16px">
          {useShiftSystem && (
            <>
              <Link
                component={DomLink}
                to={{
                  pathname: "/shift",
                  state: { userRole, userName, userId }
                }}
                underline="hover"
                variant="subtitle1"
                onClick={handleNavigation}
              >
                {isOnShift ? t('End Shift') : t('Start Shift')}
              </Link>
              <Typography variant="body1" sx={{ marginLeft: 1, marginRight: 1 }}>
                |
              </Typography>
            </>
          )}
          <Link
            component={DomLink}
            to={{ pathname: "/profile", state: { userRole, userName, userId } }}
            underline="hover"
            onClick={handleNavigation}
            sx={{ marginLeft: useShiftSystem ? "10px" : "0" }}
          >
            {t('Profile')}
          </Link>
        </Box>
      </div>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: theme.zIndex.drawer + 1,
          width: '100%',
        }}
      >
        <Toolbar>
          {!isLargeScreen && (
            <IconButton
              color="inherit"
              aria-label={t("Toggle drawer")}
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <FaBars />
            </IconButton>
          )}
          
          <Typography variant="h4" noWrap component="div">
            Order Master
          </Typography>
          <Typography variant="h6" noWrap component="div" sx={{ marginLeft: '10px' }}>
            <small>{packageJson.version}</small>
          </Typography>

          <Box display="flex" alignItems="center" sx={{ marginLeft: 'auto' }}>
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              size="small"
              sx={{
                mr: 2,
                color: 'white',
                borderColor: 'rgba(255,255,255,0.6)',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.6)',
                },
                '.MuiSvgIcon-root': {
                  color: 'white',
                },
              }}
            >
              <MenuItem value="en">{t("English")}</MenuItem>
              <MenuItem value="my">{t("Burmese")}</MenuItem>
            </Select>
            <FaMoneyBillWave style={{ color: 'white', marginRight: '8px' }} />
            <Typography variant="h6" noWrap sx={{ marginRight: '10px' }}>
              {credit} {t('Credits')}
            </Typography>
            <Button
              color="inherit"
              onClick={handleSignOut}
              startIcon={<FaSignOutAlt />}
            >
              {t('Logout')}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { lg: drawerWidth }, flexShrink: { lg: 0 } }}
      >
        {/* Mobile/Tablet drawer */}
        {!isLargeScreen && (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: drawerWidth,
                marginTop: '50px',
                height: 'calc(100% - 50px)'
              },
            }}
          >
            {drawerContent}
          </Drawer>
        )}
        
        {/* Desktop drawer */}
        {isLargeScreen && (
          <Drawer
            variant="permanent"
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                marginTop: '50px',
                height: 'calc(100% - 50px)'
              },
            }}
            open
          >
            {drawerContent}
          </Drawer>
        )}
      </Box>

      <Container
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          marginTop: '64px',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {children}
      </Container>
    </Box>
  );
};

export default Layout;
